/**
 * P2B2: choose a destination for an ordinary author material IN the existing
 * publication dialog. Selection is read-only until the parent confirmation.
 * No Reader/publish calls, new articles, BookPages or QA MASTER mutation.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  availablePaidVolumes, inBookVolume, inBookSection, confirmedBookPlacement,
} from "../../lib/editorialBookDestinationV1.mjs";

function DestinationField({ title, value, options, placeholder, onChange, disabled, testId }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-espresso/70">{title}</div>
      <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-11 rounded-xl border-espresso/20 bg-white text-espresso"
          data-testid={testId}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="z-[110] max-h-64 rounded-xl border-espresso/15 bg-[#FAF5EC]">
          {options.map((item) => (
            <SelectItem key={item.id} value={item.id}
              className="rounded-lg text-espresso focus:bg-[#EDE0CD] focus:text-espresso">
              {item.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function EditorialBookDestinationPickerV1({ onSelectionChange, disabled = false, materialId = null, materialTitle = "" }) {
  const [volumes, setVolumes] = useState([]);
  const [sections, setSections] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [volumeId, setVolumeId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [subsectionId, setSubsectionId] = useState("");
  const [pages, setPages] = useState([]);
  const [pageChoice, setPageChoice] = useState("");
  const [pagesLoading, setPagesLoading] = useState(false);
  const NEW_PAGE = "__create_author_page__";
  const [volumesLoading, setVolumesLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [subsectionsLoading, setSubsectionsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setVolumesLoading(true);
    setError("");
    api.get("/admin/book/volumes").then(({ data }) => {
      if (active) setVolumes(availablePaidVolumes(data));
    }).catch(() => {
      if (active) setError("Не удалось загрузить места публикации. Закройте окно и повторите попытку.");
    }).finally(() => { if (active) setVolumesLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setSections([]);
    setSectionId("");
    setSubsections([]);
    setSubsectionId("");
    setPages([]);
    setPageChoice("");
    if (!volumeId) return;
    let active = true;
    setSectionsLoading(true);
    setError("");
    api.get(`/admin/book/sections?volume_id=${encodeURIComponent(volumeId)}`)
      .then(({ data }) => { if (active) setSections(inBookVolume(data, volumeId)); })
      .catch(() => { if (active) setError("Не удалось загрузить разделы Тома."); })
      .finally(() => { if (active) setSectionsLoading(false); });
    return () => { active = false; };
  }, [volumeId]);

  useEffect(() => {
    setSubsections([]);
    setSubsectionId("");
    setPages([]);
    setPageChoice("");
    if (!volumeId || !sectionId) return;
    let active = true;
    setSubsectionsLoading(true);
    setError("");
    api.get(`/admin/book/subsections?volume_id=${encodeURIComponent(volumeId)}&section_id=${encodeURIComponent(sectionId)}`)
      .then(({ data }) => { if (active) setSubsections(inBookSection(data, volumeId, sectionId)); })
      .catch(() => { if (active) setError("Не удалось загрузить категории."); })
      .finally(() => { if (active) setSubsectionsLoading(false); });
    return () => { active = false; };
  }, [volumeId, sectionId]);

  useEffect(() => {
    setPages([]);
    setPageChoice("");
    if (!volumeId || !sectionId || !subsectionId) return;
    let active = true;
    setPagesLoading(true);
    setError("");
    api.get(`/admin/book/pages?volume_id=${encodeURIComponent(volumeId)}&section_id=${encodeURIComponent(sectionId)}&subsection_id=${encodeURIComponent(subsectionId)}`)
      .then(({ data }) => {
        if (active) setPages(Array.isArray(data) ? data.filter((p) =>
          p.volume_id === volumeId && p.section_id === sectionId &&
          p.subsection_id === subsectionId && p.parent_kind === "subsection"
        ) : []);
      })
      .catch(() => { if (active) setError("Не удалось загрузить страницы выбранной категории."); })
      .finally(() => { if (active) setPagesLoading(false); });
    return () => { active = false; };
  }, [volumeId, sectionId, subsectionId]);

  const paidVolumes = availablePaidVolumes(volumes);
  const availableSections = inBookVolume(sections, volumeId);
  const availableCategories = inBookSection(subsections, volumeId, sectionId);
  const availablePages = pages.filter((p) =>
    !p.editorial_material_id || p.editorial_material_id === materialId
  );
  const chosenPage = availablePages.find((p) => p.id === pageChoice);
  const newPageSelected = pageChoice === NEW_PAGE;
  const selection = pageChoice && !volumesLoading && !sectionsLoading && !subsectionsLoading && !pagesLoading && !error
    ? confirmedBookPlacement({
        volumeId, sectionId, subsectionId,
        pageId: newPageSelected ? null : pageChoice,
      }, paidVolumes, availableSections, availableCategories, availablePages)
    : null;
  const bookName = paidVolumes.find((v) => v.id === volumeId)?.title;
  const spaceName = availableSections.find((v) => v.id === sectionId)?.title;
  const categoryName = availableCategories.find((v) => v.id === subsectionId)?.title;
  const pageName = newPageSelected ? (materialTitle || "Новая статья") : chosenPage?.title;
  // No persistence on changing a select, opening or closing the dialog.
  // The parent makes ONE explicit confirmation request to P2B1.
  useEffect(() => {
    onSelectionChange(selection);
  }, [onSelectionChange, selection?.book_volume_id, selection?.section_id, selection?.subsection_id, selection?.page_id]);

  return (
    <section className="mb-4 rounded-xl border border-espresso/15 bg-white/65 p-4"
      data-testid="editorial-book-destination-picker">
      <div className="font-serif text-lg text-espresso">Место публикации</div>
      <p className="mb-3 mt-1 text-xs leading-relaxed text-espresso/65">
Выберите место в книге. После успешной публикации статья появится в выбранной карточке.
      </p>
      {volumesLoading && <p className="text-sm text-espresso/60">Загрузка разделов книги…</p>}
      {error && <p className="text-sm text-red-800" role="alert">{error}</p>}
      {!volumesLoading && !error && paidVolumes.length === 0 && (
        <p className="text-sm text-espresso/65" data-testid="editorial-book-no-volumes">
          В книге пока нет доступной структуры публикации. Ничего не создано автоматически.
        </p>
      )}
      {!volumesLoading && !error && paidVolumes.length > 0 && (
        <div className="space-y-3">
          <DestinationField title="Продукт / Том" value={volumeId}
            options={paidVolumes} placeholder="Выберите Том"
            disabled={disabled} onChange={setVolumeId} testId="editorial-book-volume" />
          <DestinationField title="Раздел" value={sectionId}
            options={availableSections} placeholder={sectionsLoading ? "Загрузка…" : "Выберите раздел"}
            disabled={disabled || !volumeId || sectionsLoading || !availableSections.length}
            onChange={setSectionId} testId="editorial-book-section" />
          <DestinationField title="Категория" value={subsectionId}
            options={availableCategories} placeholder={subsectionsLoading ? "Загрузка…" : "Выберите категорию"}
            disabled={disabled || !sectionId || subsectionsLoading || !availableCategories.length}
            onChange={setSubsectionId} testId="editorial-book-category" />
          <DestinationField title="Карточка темы" value={pageChoice}
            options={[
              { id: NEW_PAGE, title: `Новая карточка «${materialTitle || "Новый материал"}»` },
              ...availablePages,
            ]}
            placeholder={pagesLoading ? "Загрузка…" : "Выберите существующую карточку или создайте новую"}
            disabled={disabled || !subsectionId || pagesLoading || !!error}
            onChange={setPageChoice} testId="editorial-book-page" />
          {selection && (
            <p className="text-xs text-espresso/70" data-testid="editorial-book-destination-ready">
              <span className="block font-medium">Расположение карточки:</span>
              <span className="block mt-1" data-testid="editorial-book-destination-path">
                {[bookName, spaceName, categoryName, pageName].filter(Boolean).join(" → ")}
              </span>
              <span className="block mt-1">Подтвердите выбор кнопкой внизу.</span>
            </p>
          )}
        </div>
      )}
    </section>
  );
}
